/** The Game room protocol (`/ws/game`) plus its two pre-connect REST endpoints. */

import type { PROTOCOL_VERSION } from "./endpoints";
import type { PingMessage, PongMessage } from "./room-core";
import type { PromptOption, PromptRequest } from "./encounter";
import type { ChatAppendedMessage, ChatPostMessage, FeedEntry } from "./feed";
import type { Directive, GameFxMessage, GameSetViewMessage } from "./directives";

/**
 * The pre-connect **slot-list** REST endpoint. A client
 * GETs this *before* opening any websocket to populate the dropdown-login: it
 * returns the game name, every slot (id + name + role + whether it's currently
 * actively connected), and whether a room password is required. No auth — it
 * advertises only public lobby metadata, never the password hash or any token.
 */
export const GAME_SLOTS_PATH = "/api/game/slots" as const;
/**
 * The pre-connect **credential→token login** REST endpoint. A client POSTs the same
 * credential it would have put in a `game_hello`
 * (a reclaim `token`, the GM `hostKey`, or a player `slotId` + optional `password`)
 * and the server runs the *same* checks the `/ws/game` hello does (argon2 password
 * verify, host-key compare, token lookup) — minting/reclaiming the slot's token from
 * the one source of truth (`session.json`'s `tokens` map) — and returns it. The
 * client then opens `/ws/game` with a **token-only** hello. On valid
 * credentials → `200 {@link GameLoginResponse}`; on bad credentials → `401`
 * carrying a {@link GameRejectReason}.
 *
 * **No slot lock here.** Login mints/returns a token *without* locking the slot, and
 * runs no availability check — the slot's single-live-connection presence is settled at
 * ws-open by **reclaim-by-eviction** (the token presented in `game_hello` evicts any
 * incumbent socket; last-connection-wins), never a reject.
 */
export const GAME_LOGIN_PATH = "/api/game/login" as const;

// ── Game room protocol (dropdown login + slot lifecycle) ─────────────────────
//
// The Game room (`/ws/game`) is the durable-game **identity gate**. Unlike the
// roster/encounter rooms — whose `join` hands out an ephemeral `Pilot-N` id — the
// Game room authenticates a client against the GM-defined slots and binds it to a
// stable **slot id** (which becomes the `actorId` it uses on the other rooms).
//
// The login is a Foundry/Minecraft handshake: a player picks a named slot from a
// dropdown + an optional room password; the GM authenticates as the operator with a
// host/admin key. Slots are **locked**: one active
// connection each, reclaimed on reconnect via a **remembered token** the server
// issues on first join and the client stores in localStorage. The `GameState` and
// `Slot` shapes themselves are opaque here (typed `unknown`) — they live in
// `@lancer-vtt/domain`, which depends on `shared/` (so `shared/` can't import them
// back) — exactly as the roster/encounter rooms keep their state opaque on the wire.

/**
 * Public lobby metadata for one slot, as advertised by the slot-list REST endpoint
 * (`GAME_SLOTS_PATH`). `taken` is whether the slot currently has a *live* socket
 * holding it (so the dropdown can grey it out). Never carries the password hash or
 * the slot's remembered token.
 */
export type SlotListEntry = {
  id: string;
  name: string;
  /** The slot's role — "gm" or "player" (a `SlotRole` in the domain). */
  role: string;
  /** True when a live connection currently holds this slot (locked). */
  taken: boolean;
}

/**
 * The slot-list response (`GET /api/game/slots`) — the pre-connect dropdown-login
 * payload. Advertises the game name, every slot's public metadata, and whether a
 * room password is required, with no auth. The client renders the dropdown from
 * `slots` and shows a password field iff `passwordRequired`.
 */
export type SlotListResponse = {
  gameName: string;
  slots: SlotListEntry[];
  /** True when a room password is set (the client must supply one to join a player slot). */
  passwordRequired: boolean;
}

/**
 * The opening client→server message on `/ws/game`: present an already-minted reclaim
 * **token** and bind this socket to its slot. The ws hello is **token-only** — the
 * credential verify (token / GM host key / player slot+password) lives at
 * `POST /api/game/login` ({@link GAME_LOGIN_PATH}), which mints the token the client
 * presents here. This aligns `/ws/game` with the roster/encounter token handshake: the
 * socket asserts no credential, only a token the server already issued.
 *
 * The server finds the slot that minted the token, **reclaims** it for this socket, and
 * **kicks any stale socket** still holding it (single clear presence). An absent/unknown
 * token is rejected (`game_reject`, then close). There is no `slotId`/`hostKey`/`password`
 * branch on the ws path — those go to the REST endpoint.
 */
export type GameHelloMessage = {
  type: "game_hello";
  /** The reclaim token issued by `POST /api/game/login` — the only ws credential. */
  token: string;
  /**
   * A **tool** connection (the `lvtt` CLI): it rides alongside the slot's holder instead of
   * evicting it, and is not presence (no `connected` entry, no join/leave feed line). It receives
   * everything the slot receives (`game_change`, its undo state, replies), so a CLI verb run as the
   * GM never logs the GM's browser out. Omitted ⇒ an ordinary client: reclaim-by-eviction.
   */
  tool?: boolean;
}

// ── Durable-game command channel (CLI-as-ws-client; `lvtt game` over /ws/game) ──
//
// The `lvtt game` verbs (slot admin + room password + view-state) drive the durable
// `GameState` as a **ws client of the authoritative server** — the GM authenticates
// with a token-only `game_hello`, then issues these
// over the SAME `/ws/game` socket. Slot admin is **GM-only** (the server gates it on
// the authenticated slot's role), so only the GM-authenticated socket may apply.
//
// Two reasons this isn't a single generic envelope like `roster_command`:
//  - the room password must be **hashed server-side** (`backend/src/passwords.ts`) —
//    the CLI never runs argon2 on this path. So `game_set_password` carries the
//    **plaintext** (or null to clear) and the server hashes it into the
//    `set_room_password` command. The plaintext never enters the command/state/file.
//  - `check-password` is a server-side **verify** with no state change — its own
//    request/response pair (`game_check_password` → `game_password_check`).
// Every other command (define/remove/role/view-state) rides the generic `game_command`.

/**
 * GM→server: apply one durable Game command (define/remove slot, set slot role, set
 * slot view-state). The `command` is opaque on the wire (the domain narrows it to a
 * `GameCommand`); `set_room_password` is **excluded** — it goes through
 * `game_set_password` so the server hashes the plaintext. Attributed to the
 * authenticated slot id; the server gates on it (GM-only) and broadcasts `game_change`.
 */
export type GameCommandMessage = {
  type: "game_command";
  /** Vestigial: the server ignores it and attributes the command to the socket-bound slot id (gameRoom.ts dispatch). Kept only for wire compatibility. */
  actorId: string;
  /** The `GameCommand` to apply (opaque to `shared/`; never a `set_room_password`). */
  command: unknown;
}

/** GM→server: undo this actor's last durable Game command. `actorId` is vestigial: the server uses the socket-bound slot id. */
export type GameUndoMessage = { type: "game_undo"; actorId: string }
/** GM→server: redo this actor's last undone durable Game command. `actorId` is vestigial: the server uses the socket-bound slot id. */
export type GameRedoMessage = { type: "game_redo"; actorId: string }

/**
 * GM→server: set (or clear, with `null`) the **room password**. The server hashes the
 * plaintext (argon2id, `backend/src/passwords.ts`) and applies `set_room_password`
 * with the digest — the plaintext never enters the command, state, or world folder.
 * Attributed to the authenticated slot; GM-only; broadcasts `game_change`.
 */
export type GameSetPasswordMessage = {
  type: "game_set_password";
  /** The plaintext to hash and store, or `null` to clear the password (open join). */
  password: string | null;
}

/**
 * GM→server: verify a plaintext against the stored room-password hash. A read-only
 * check (no state change) — the server replies with a `game_password_check`. GM-only.
 */
export type GameCheckPasswordMessage = {
  type: "game_check_password";
  actorId: string;
  password: string;
}

/**
 * GM→server: delete an encounter room — a **lifecycle** delete, NOT a `game_command`.
 * Its own dedicated request/response pair for the same
 * reason `game_set_password` has one: the server must do **async work the reducer
 * can't** before the command is well-formed — here, capturing the room's full raw
 * content (state + forward log) and dropping the in-memory room + its persisted files
 * — so the client sends only the thin intent (`encounterId`), never the captured
 * payload. The server builds the actual `delete_room` `GameCommand` (with the capture
 * embedded, so its `restore_room` inverse round-trips losslessly) and applies it
 * through the normal Game command bus. GM-only; broadcasts `game_change` on success.
 * A `null`/unknown `encounterId` is a no-op rejection (no broadcast), like any other
 * reducer-rejected command.
 */
export type GameDeleteRoomMessage = {
  type: "game_delete_room";
  encounterId: string;
}

/**
 * GM→server: wipe the whole world back to a fresh table in one step — a **lifecycle**
 * command, NOT a `game_command`, for the same reason
 * `game_delete_room` isn't: the server must do async work the reducer can't (capture +
 * wipe the whole roster and every encounter room) before the `reset_world` command is
 * even well-formed, so the client sends only the bare intent, never the captured
 * payload. The server builds the actual `reset_world` `GameCommand` (with the capture
 * embedded, so its `restore_world` inverse round-trips losslessly) and applies it
 * through the normal Game command bus. GM-only; broadcasts `game_change` (this socket
 * + every other) and a `roster_change` (on `/ws/roster`) on success. Preserves the GM
 * slot(s), the game name/password, and the loaded packs.
 */
export type GameResetWorldMessage = {
  type: "game_reset_world";
}

export type GameClientMessage =
  | GameHelloMessage
  | ChatPostMessage
  | GameSetViewMessage
  | GameFxMessage
  | GameCommandMessage
  | GameUndoMessage
  | GameRedoMessage
  | GameSetPasswordMessage
  | GameCheckPasswordMessage
  | GameDeleteRoomMessage
  | GameResetWorldMessage
  | PingMessage;

/**
 * Sent on a successful `game_hello`: the client is now authenticated as `slotId`
 * (its `actorId` for every other room). Carries the **remembered token** to store
 * in localStorage for reconnect (always sent — on first join it's freshly minted,
 * on a token reclaim it's the same token echoed back), the full authoritative raw
 * `GameState` (opaque here — the domain narrows it), and this slot's standing
 * `viewState` (re-sent on reconnect so a dropped player lands where the GM put them;
 * the `SlotViewState` shape is opaque on the wire).
 */
export type GameWelcomeMessage = {
  type: "game_welcome";
  protocol: typeof PROTOCOL_VERSION;
  /** The slot this client authenticated as — its actor id for every room. */
  slotId: string;
  /** The slot's role ("gm" | "player"). */
  role: string;
  /** The reclaim token to remember (localStorage); presented on reconnect. */
  token: string;
  /** The viewer-projected `PublicGameState`, NOT the raw `GameState` (opaque to `shared/`). */
  state: unknown;
  /** This slot's standing view-state, re-sent on (re)join (opaque to `shared/`). */
  viewState: unknown;
  /**
   * The bounded **feed tail** (last {@link FEED_TAIL_CAP} entries, oldest→newest) — the
   * recent chat/system/roll history a joining client loads with no separate fetch.
   * Full history lives in the world folder's `feed.jsonl`.
   */
  feed: FeedEntry[];
}

/**
 * Why a login was rejected — a closed-but-explained auth failure, shared by the REST
 * `POST /api/game/login` `401` body and the ws `game_reject`. There is **no**
 * `slot_taken`: the Game-room slot is reclaim-by-eviction, not reject-the-second.
 */
export type GameRejectReason =
  | "bad_password"
  | "unknown_slot"
  | "bad_token"
  | "bad_host_key"
  | "missing_credentials";

/**
 * Sent when a token-only `game_hello` fails to verify (a bad/expired token). Credential
 * rejections (wrong password / host key, unknown slot) surface at `POST /api/game/login`
 * as a `401`; this frame covers the ws token-verify path. The client surfaces `reason`
 * in the login UI; the server then closes the socket.
 */
export type GameRejectMessage = {
  type: "game_reject";
  reason: GameRejectReason;
}

/**
 * The request body for `POST /api/game/login` ({@link GAME_LOGIN_PATH}). Carries the
 * same three-way credential as a `game_hello` (validated server-side in the same
 * priority order: token → hostKey → slot+password), minus the `type` discriminator.
 */
export type GameLoginRequest = {
  /** A remembered reclaim token from a prior login (reconnect path). */
  token?: string;
  /** The GM operator/host key (authenticate as GM). */
  hostKey?: string;
  /** The player slot id being claimed from the dropdown. */
  slotId?: string;
  /** The plaintext room password, checked server-side against the stored hash. */
  password?: string;
}

/**
 * The `200` body for a successful `POST /api/game/login`: the slot the credential
 * resolved to, its role, and the (minted-if-absent, persisted) reclaim token the
 * client then presents in a token-only `game_hello`. No slot was locked issuing this.
 */
export type GameLoginResponse = {
  /** The reclaim token to remember and present on the ws hello. */
  token: string;
  /** The slot this credential authenticated as — its actor id for every room. */
  slotId: string;
  /** The slot's role ("gm" | "player"). */
  role: string;
}

/**
 * The error body for a failed `POST /api/game/login` (`401`): the typed reason the
 * credential was rejected, mirroring `game_reject.reason` so the client maps both the
 * REST and ws rejections through the same copy.
 */
export type GameLoginErrorResponse = {
  reason: GameRejectReason;
}

/**
 * Broadcast to every connected Game-room socket after a change to who's connected
 * (a join, a leave, or a reclaim that kicked a stale socket) **or** a change to the
 * durable `GameState`. Carries the viewer-projected `PublicGameState` — **not** the
 * raw `GameState` — so each client's lobby/roster view reconciles, in the same
 * whole-state LWW shape the other rooms use. Both this and `game_welcome` are fed by
 * `GameRoom.publicState()`, the one helper that projects; the raw state is never sent
 * to a socket.
 */
export type GameChangeMessage = {
  type: "game_change";
  /** The new state as the viewer-projected `PublicGameState`, not raw (opaque to `shared/`). */
  state: unknown;
  /** The slot ids currently held by a live connection (for a live "who's here"). */
  connected: string[];
}

/**
 * Sent to a socket whose slot was severed by the server. Two distinct reasons share
 * this one message — the client's stay-down
 * semantics (`abandonedRef`) apply to both, so this is a `reason` field, NOT a second
 * message type:
 *   - `"evicted"` — the slot was **reclaimed by another connection** (reconnect kicks
 *     the stale connection). The kicked client shows "reconnected
 *     elsewhere" and stops; the server closes its socket right after. Distinct from
 *     `game_reject` (which fails an *incoming* hello) — this evicts an *established* one.
 *   - `"removed"` — the bound slot no longer exists in `state.slots` (the GM removed
 *     it, directly or via undo/redo). The server closes the socket AND revokes the
 *     slot's reclaim token (`evictOrphanedConnections`) — a stray "remove slot" +
 *     later "redefine a slot with the same id" must not let the old token back in.
 */
export type GameKickedMessage = {
  type: "game_kicked";
  reason: "evicted" | "removed";
}

/**
 * Pushed to a client when the GM updates its **standing view-state** live (the
 * durable half). Carries the slot's new full `viewState` so the
 * client's effect registry re-derives its standing effects (navigate/banner). This
 * is the *live* delivery; the *durable* delivery is `game_welcome.viewState` on the
 * next (re)connect — both carry the same shape. Only sent to a targeted, connected
 * slot.
 */
export type GameViewMessage = {
  type: "game_view";
  /** The slot this update is for (its own slot id). */
  slotId: string;
  /** The slot's new standing view-state (opaque `SlotViewState` to `shared/`). */
  viewState: unknown;
}

/**
 * Pushed to a client when the GM fires an **ephemeral FX** at it (the fire-and-forget
 * half). The client's effect registry runs the handler for
 * `directive.type` **once**. NOT persisted and NOT replayed — a client that wasn't
 * connected when it fired never receives it.
 */
export type GameFxEventMessage = {
  type: "game_fx_event";
  directive: Directive;
}

// ── Ephemeral decision-card channel ──────────────────────────────────────────────────
//
// When a cross-actor reaction/decision (Brace, Overwatch, an authored offer) halts
// resolution awaiting an answer, the ORIGINATING `/ws/encounter` room already ships an
// `EncounterPromptMessage` to the decider(s) (a floating window, live only where COMBAT.app
// is mounted). `game_decision` is the SAME decision, bridged server-side onto `/ws/game` so
// it also renders as an interactive card in COMMS.app's feed — reachable even from a
// home-shell mount with no encounter socket (awareness-only there; the card's buttons act
// only where a live encounter controller exists to re-submit the answer).
//
// **Ephemeral, exactly like `game_fx`/`game_fx_event` above — NOT a `FeedEntry`, NEVER
// written to `feed.jsonl`, no reducer, no inverse, no replay**: a durable
// "pending decision" would be a restart/replay zombie (the prompt is re-derived fresh, never
// restored). `game_decision_resolved` is the ephemeral teardown twin, fired on resolution so
// every client clears/unpins the card — the encounter room already auto-dismisses the prompt
// WINDOW the same way.

/**
 * Broadcast on `/ws/game` when a **cross-actor** decision (`decision.who` differs from the
 * resolution's own source/attacker) halts resolution — the ephemeral COMMS.app card twin of
 * the `/ws/encounter` {@link EncounterPromptMessage} the decider's window already received.
 * Same-actor prompts (variable damage-type, Lock On) never emit this — only the acting
 * socket sees those, there is no table-relevant card to show.
 *
 * **Fog-gated** (mandatory): if the triggering resolution touched a
 * concealed/still-Hidden combatant, this is sent ONLY to the decider's slot + GM slots
 * (mirroring `EncounterPromptMessage`'s own cross-actor recipient set) — never broadcast to
 * the table, or a concealed attacker would leak. Otherwise sent to every connected slot.
 */
export type GameDecisionMessage = {
  type: "game_decision";
  /** The `choose`/reaction-offer's stable prompt id (echoes `PromptRequest.promptId`). */
  promptId: string;
  /** The decider combatant id (echoes `PromptRequest.who`). */
  who: string;
  /**
   * **Every slot that controls the decider combatant**, so the client can gate the card's
   * buttons (`decision.whoSlotIds?.includes(actorId) || role === "gm"`) — `who` above is a
   * COMBATANT id, not a slot id, and COMMS has no encounter state of its own to resolve one into
   * the other. Absent only when the deciding
   * combatant has no controlling slot at all (a bare/test room with no roles, or a GM-owned
   * NPC with no co-drivers); a card with no `whoSlotIds` gates on GM-only.
   *
   * A list rather than a scalar as of shared control — see {@link PromptRequest.whoSlotIds}
   * for why this field goes the opposite way from the carrier it derives from.
   */
  whoSlotIds?: string[];
  /**
   * The decision's authored prompt sentence — verbatim the same text
   * {@link PromptRequest.prompt} carries for the floating window (e.g. Brace's "Brace?
   * Resistance to all damage…"). The card replaces it with `title` + tiles + `summary` only for a
   * decision carrying all three plus `incomingDamage` (Brace; `hasReactionStakes`); every other
   * decision, Intercede and authored damage offers included, shows this sentence. Never hardcoded
   * per-reaction copy on the client.
   */
  kind: string;
  /** The decision's short name (`Brace`) — echoes {@link PromptRequest.title}; the card's question headline reads it. */
  title?: string;
  /** The optional one-line cost summary — echoes {@link PromptRequest.summary}. */
  summary?: string;
  /** The selectable options (id + label) the card's buttons present. Echoes `PromptRequest.options`. */
  options: PromptOption[];
  /**
   * The decision's answer shape — echoes {@link PromptRequest.answerKind}; absent means `"options"`.
   * A `"cell"` or `"combatant"` card highlights the hovered option's space or unit on the board.
   */
  answerKind?: PromptRequest["answerKind"];
  /** The already-rolled incoming damage, if any — the exact `PromptRequest.incomingDamage` shape, reused verbatim. */
  incomingDamage?: PromptRequest["incomingDamage"];
  /**
   * Combatant id → display name, for every live combatant (mirrors the resolution-feed
   * bridge's own `names` map) — COMMS.app on `/ws/game` has no encounter state of its own, so
   * `who`/`source` resolve to a name here, server-side, at broadcast time.
   */
  names: Record<string, string>;
  /**
   * The resolution's own acting source (the attacker/mover whose action raised the
   * decision), if distinct from `who` — lets the card read "React to `names[source]`'s
   * attack?" Absent only where no meaningful acting source exists.
   */
  source?: string;
}

/**
 * Broadcast on `/ws/game` when a decision (Brace, Overwatch, an authored offer) resolves —
 * the teardown signal for {@link GameDecisionMessage}: every client clears/unpins the card
 * named by `promptId`. Fog-gated **the same way** as the card it tears down (sent to the
 * same recipients the matching `game_decision` reached). The `/ws/encounter` prompt WINDOW
 * still auto-dismisses via the existing resolution broadcast, unchanged — this is the COMMS
 * card's own teardown, a separate wire signal because the card can outlive the window (it's
 * visible table-wide, not just to the decider). Inherits the accepted double-answer race
 * unchanged: whichever answer resolves first fires this once.
 */
export type GameDecisionResolvedMessage = {
  type: "game_decision_resolved";
  promptId: string;
}

/**
 * Sent to the issuing socket after a durable Game command/undo/redo: this actor's
 * undo/redo availability (the same shape as the roster/encounter `undo_state`, but a
 * distinct `type` so the game server-message union stays self-contained). Lets the
 * CLI/UI surface its undo affordances without tracking the stacks itself.
 */
export type GameUndoStateMessage = {
  type: "game_undo_state";
  canUndo: boolean;
  canRedo: boolean;
  /** What the next undo would reverse (top-of-stack command type), if any. */
  undoLabel: string | null;
  /** What the next redo would re-apply (top-of-stack command type), if any. */
  redoLabel: string | null;
}

/**
 * Reply to a `game_check_password`: whether the supplied plaintext matched the stored
 * room-password hash (server-side argon2id verify). A read-only answer — no state change.
 */
export type GamePasswordCheckMessage = {
  type: "game_password_check";
  valid: boolean;
}

/**
 * Reply to a successful `game_delete_room` **or** a `game_redo` that re-applies a
 * previously-undone `delete_room`, to the issuing socket only. A **dedicated success
 * ack** — deleting a room that ISN'T on the table
 * leaves `GameState` byte-identical (rooms aren't modeled in `GameState` at all), so a
 * driver can't tell success from a silent rejection/no-op by diffing `game_change` the
 * way every other Game command does (mirrors why `game_password_check` is its own reply
 * rather than inferred from a state diff). Not sent on a `game_delete_room` rejection
 * (unknown id, or no room-lifecycle I/O wired) — the driver's absence-of-ack IS the
 * failure signal, exactly like a `game_command` that gets no `game_change`.
 */
export type GameRoomDeletedMessage = {
  type: "game_room_deleted";
  encounterId: string;
}

/**
 * Sent to the issuing socket whenever a `game undo`/`game redo` round-trip actually
 * restores a room (the `restore_room` inverse of `delete_room` was applied) — the
 * mirror-image ack of {@link GameRoomDeletedMessage}. Same rationale: restoring a room
 * that isn't on the table leaves `GameState` byte-identical, so `game_undo`'s
 * caller can't tell "restored a room" from "nothing to undo" by diffing state alone —
 * without this, `lvtt game undo`'s `noop` would falsely report `true` for a delete_room
 * undo that landed a *real* room restoration.
 */
export type GameRoomRestoredMessage = {
  type: "game_room_restored";
  encounterId: string;
}

/**
 * Reply to a successful `game_reset_world` **or** a `game_redo` that re-applies a
 * previously-undone reset, to the issuing socket only. A **dedicated success ack**, for
 * the same reason {@link GameRoomDeletedMessage} is one: most of what a reset changes
 * (the whole roster, every encounter room) lives
 * entirely outside `GameState`, so a driver can't reliably infer "did this apply" from
 * a `game_change` state diff alone — a reset with no non-GM slots and no active
 * encounter leaves `GameState` byte-identical even though the roster/rooms were wiped.
 * Not sent on a rejection.
 */
export type GameWorldResetMessage = {
  type: "game_world_reset";
}

/**
 * Sent to the issuing socket whenever a `game undo`/`game redo` round-trip actually
 * restores the whole world (the `restore_world` inverse of `reset_world` was applied)
 * — the mirror-image ack of {@link GameWorldResetMessage}, parallel to how
 * {@link GameRoomRestoredMessage} mirrors {@link GameRoomDeletedMessage}.
 */
export type GameWorldRestoredMessage = {
  type: "game_world_restored";
}

export type GameServerMessage =
  | GameWelcomeMessage
  | ChatAppendedMessage
  | GameRejectMessage
  | GameChangeMessage
  | GameKickedMessage
  | GameViewMessage
  | GameFxEventMessage
  | GameDecisionMessage
  | GameDecisionResolvedMessage
  | GameUndoStateMessage
  | GamePasswordCheckMessage
  | GameRoomDeletedMessage
  | GameRoomRestoredMessage
  | GameWorldResetMessage
  | GameWorldRestoredMessage
  | PongMessage;